/**
 * The database before it is open: empty, and nothing can be put in it.
 *
 * The browser opens SQLite only for a session that needs it (`../db.ts`): a
 * signed-out page never downloads the WebAssembly. Two pieces of the phone's
 * code still reach the database there. `ActiveTenantProvider` reads the
 * active business while it draws, and `MeProvider` wipes the device the
 * moment the session reads signed out (`forgetDevice`). Against a database
 * that holds nothing, the read answers no row and the wipe has nothing to do,
 * so this answers exactly that: every read is empty, and a clear (`DELETE`,
 * `DROP`) or a transaction's own statements do nothing.
 *
 * Anything that would store something throws instead. A write here would be
 * lost when the real database opens, and the gate in
 * `~/app/root-providers.tsx` is meant to make that unreachable: a session
 * that can write waits for the real database.
 */
import type { BindParams, ExecuteResult, RawExecuteResult } from './adapter';

/** The statements an empty database can answer without holding anything. */
const HARMLESS = /^(select|with|pragma|explain|delete|drop|begin|commit|end|rollback|savepoint|release)\b/;

/** The first keyword of a statement, past comments and opening brackets. */
export const leadingKeyword = (sql: string): string =>
  sql
    .replace(/^(?:\s+|--[^\n]*\n?|\/\*[\s\S]*?\*\/|\()+/, '')
    .slice(0, 16)
    .toLowerCase();

const check = (sql: string): void => {
  if (!HARMLESS.test(leadingKeyword(sql))) {
    throw new Error(`The database is not open yet, so this cannot be stored: ${sql.slice(0, 80)}`);
  }
};

const NOTHING = { changes: 0, lastInsertRowId: 0 } as const;

class EmptyStatement {
  constructor(readonly sql: string) {}

  executeSync<T>(_params?: BindParams): ExecuteResult<T> {
    check(this.sql);
    return { ...NOTHING, getAllSync: () => [], getFirstSync: () => null, resetSync: () => undefined };
  }

  executeForRawResultSync(_params?: BindParams): RawExecuteResult {
    check(this.sql);
    return { ...NOTHING, getAllSync: () => [], getFirstSync: () => null, resetSync: () => undefined };
  }

  finalizeSync(): void {}
}

/** The same calls the drizzle driver and `../db.ts` make on `WebSqliteDatabase`. */
export class EmptySqliteDatabase {
  prepareSync(sql: string): EmptyStatement {
    return new EmptyStatement(sql);
  }

  execSync(sql: string): void {
    for (const statement of sql.split(';')) if (statement.trim() !== '') check(statement);
  }

  getAllSync<T>(sql: string, _params?: BindParams): T[] {
    check(sql);
    return [];
  }

  getFirstSync<T>(sql: string, _params?: BindParams): T | null {
    check(sql);
    return null;
  }

  runSync(sql: string, _params?: BindParams): { changes: number; lastInsertRowId: number } {
    check(sql);
    return { ...NOTHING };
  }
}
